import type { SessionDto, UserRole } from '@cpm/shared';
import { KeyRound, Laptop, ShieldCheck, ShieldOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  useConfirmTwoFactor,
  useDisableTwoFactor,
  useInvalidateTwoFactor,
  useRegenerateRecoveryCodes,
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
  useSetTwoFactorPolicy,
  useStartTwoFactor,
  useTwoFactorPolicy,
  useTwoFactorStatus,
} from '../api/security.js';
import { QrCode } from './QrCode.js';
import { Modal } from './Modal.js';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorNote, Input, Label, Spinner } from './ui.js';

/**
 * Shows recovery codes exactly once. The caller must persist nothing — only
 * the hashes exist server-side, so this render is the user's only chance.
 */
export function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  return (
    <div className="rounded-lg bg-amber-50 p-4 ring-1 ring-amber-600/20 dark:bg-amber-500/10">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        Save these recovery codes now
      </p>
      <p className="mt-0.5 text-xs text-amber-700/90 dark:text-amber-400/90">
        Each code works once if you lose your authenticator. They are shown only this once.
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-slate-800 dark:text-slate-200">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(text);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <a
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
          download="recovery-codes.txt"
          className="inline-flex items-center justify-center rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600"
        >
          Download
        </a>
      </div>
    </div>
  );
}

/**
 * The enrolment flow: request a secret, show the QR + manual key, confirm with
 * a code, then reveal one-time recovery codes. Reused by the Security page and
 * the forced-enrolment gate. `onDone` fires when the user acknowledges.
 */
export function TwoFactorEnrolFlow({ onDone }: { onDone: () => void }) {
  const start = useStartTwoFactor();
  const confirm = useConfirmTwoFactor();
  const [code, setCode] = useState('');

  // Kick off enrolment once on mount (a ref guards StrictMode's double effect).
  const startMutate = start.mutate;
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    startMutate();
  }, [startMutate]);

  if (start.isPending || start.isIdle) return <Spinner label="Preparing enrolment…" />;
  if (start.error) return <ErrorNote error={start.error} />;

  if (confirm.data) {
    return (
      <div className="space-y-4">
        <RecoveryCodes codes={confirm.data.recoveryCodes} />
        <Button className="w-full" onClick={onDone}>
          I've saved my recovery codes
        </Button>
      </div>
    );
  }

  const setup = start.data!;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        confirm.mutate({ code });
      }}
    >
      <div className="flex flex-col items-center gap-3">
        <QrCode value={setup.otpauthUrl} />
        <p className="text-center text-sm text-slate-500 dark:text-slate-400">
          Scan this with an authenticator app (Google Authenticator, Aegis, 1Password…).
        </p>
      </div>
      <div>
        <Label>Or enter this key manually</Label>
        <code className="block break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm dark:bg-slate-800">
          {setup.secret}
        </code>
      </div>
      <div>
        <Label htmlFor="totp-confirm">6-digit code</Label>
        <Input
          id="totp-confirm"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      {confirm.error != null && <ErrorNote error={confirm.error} />}
      <Button type="submit" className="w-full" loading={confirm.isPending}>
        Verify and enable
      </Button>
    </form>
  );
}

/** Password re-auth modal for a destructive 2FA action (disable / regenerate). */
function ReauthModal({
  title,
  open,
  onClose,
  submitLabel,
  onSubmit,
  pending,
  error,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  submitLabel: string;
  onSubmit: (password: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const [password, setPassword] = useState('');
  return (
    <Modal title={title} open={open} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(password);
        }}
      >
        <div>
          <Label htmlFor="reauth-password">Confirm your password</Label>
          <Input
            id="reauth-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error != null && <ErrorNote error={error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Self-service 2FA card: enable, disable, and regenerate recovery codes. */
export function TwoFactorCard() {
  const status = useTwoFactorStatus();
  const disable = useDisableTwoFactor();
  const regenerate = useRegenerateRecoveryCodes();
  const invalidate = useInvalidateTwoFactor();
  const [enrolling, setEnrolling] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  return (
    <Card>
      <CardHeader
        title="Two-factor authentication"
        description="A time-based code from your phone, required at every sign-in."
        actions={
          status.data?.enabled ? (
            <Badge tone="green">
              <ShieldCheck className="mr-1 size-3.5" /> On
            </Badge>
          ) : status.data?.required ? (
            <Badge tone="yellow">Required</Badge>
          ) : (
            <Badge tone="gray">Off</Badge>
          )
        }
      />
      <div className="p-5">
        {status.isPending ? (
          <Spinner />
        ) : status.error ? (
          <ErrorNote error={status.error} />
        ) : enrolling ? (
          // Checked before the enabled/off views so the one-time recovery
          // codes survive 2FA flipping on mid-flow.
          <TwoFactorEnrolFlow
            onDone={() => {
              setEnrolling(false);
              invalidate();
            }}
          />
        ) : status.data.enabled ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Two-factor is on. You have{' '}
              <span className="font-medium">{status.data.recoveryCodesRemaining}</span> recovery
              code{status.data.recoveryCodesRemaining === 1 ? '' : 's'} left.
            </p>
            {regenerate.data ? (
              <RecoveryCodes codes={regenerate.data.recoveryCodes} />
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setRegenOpen(true)}>
                  <KeyRound className="size-4" /> Regenerate recovery codes
                </Button>
                <Button
                  variant="secondary"
                  className="hover:!text-red-600"
                  onClick={() => setDisableOpen(true)}
                  disabled={status.data.required}
                  title={
                    status.data.required
                      ? 'Your role requires two-factor; it cannot be disabled.'
                      : undefined
                  }
                >
                  <ShieldOff className="size-4" /> Disable
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {status.data.required
                ? 'Your role requires two-factor authentication.'
                : 'Add a second factor to protect your account.'}
            </p>
            <Button onClick={() => setEnrolling(true)}>
              <ShieldCheck className="size-4" /> Enable two-factor
            </Button>
          </div>
        )}
      </div>

      <ReauthModal
        title="Disable two-factor"
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        submitLabel="Disable"
        pending={disable.isPending}
        error={disable.error}
        onSubmit={(password) =>
          disable.mutate(
            { password },
            { onSuccess: () => setDisableOpen(false) },
          )
        }
      />
      <ReauthModal
        title="Regenerate recovery codes"
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        submitLabel="Regenerate"
        pending={regenerate.isPending}
        error={regenerate.error}
        onSubmit={(password) =>
          regenerate.mutate({ password }, { onSuccess: () => setRegenOpen(false) })
        }
      />
    </Card>
  );
}

function SessionRow({
  session,
  onRevoke,
  revoking,
}: {
  session: SessionDto;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <tr>
      <td className="px-5 py-3">
        <p className="flex items-center gap-2 font-medium">
          <Laptop className="size-4 text-slate-400" />
          {session.ip ?? 'Unknown IP'}
          {session.current && <Badge tone="blue">This device</Badge>}
        </p>
        <p className="mt-0.5 max-w-md truncate text-xs text-slate-500 dark:text-slate-400">
          {session.userAgent ?? 'Unknown device'}
        </p>
      </td>
      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
        {new Date(session.lastSeenAt).toLocaleString()}
      </td>
      <td className="px-5 py-3 text-right">
        <Button variant="ghost" className="hover:!text-red-600" loading={revoking} onClick={onRevoke}>
          {session.current ? 'Sign out' : 'Revoke'}
        </Button>
      </td>
    </tr>
  );
}

/** Self-service active-session list with individual + bulk revocation. */
export function SessionsCard() {
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const hasOthers = (sessions.data ?? []).some((s) => !s.current);

  return (
    <Card>
      <CardHeader
        title="Active sessions"
        description="Devices currently signed in to your account."
        actions={
          hasOthers ? (
            <Button variant="secondary" loading={revokeOthers.isPending} onClick={() => revokeOthers.mutate()}>
              Sign out all other sessions
            </Button>
          ) : undefined
        }
      />
      {sessions.isPending ? (
        <Spinner />
      ) : sessions.error ? (
        <div className="p-5">
          <ErrorNote error={sessions.error} />
        </div>
      ) : sessions.data.length === 0 ? (
        <EmptyState icon={<Laptop className="size-10" />} title="No active sessions" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Device</th>
                <th className="px-5 py-3 font-medium">Last active</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sessions.data.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  revoking={revoke.isPending && revoke.variables === session.id}
                  onRevoke={() => revoke.mutate(session.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(revoke.error || revokeOthers.error) != null && (
        <div className="p-5 pt-0">
          <ErrorNote error={revoke.error ?? revokeOthers.error} />
        </div>
      )}
    </Card>
  );
}

const POLICY_ROLES: Array<{ role: UserRole; label: string; description: string }> = [
  { role: 'admin', label: 'Administrators', description: 'Full control of the panel and settings' },
  { role: 'staff', label: 'Staff', description: 'Provider-side operators' },
  { role: 'client', label: 'Client portal users', description: 'Tenant logins' },
];

/**
 * Admin-only: require 2FA for whole roles. Users of a required role without
 * TOTP are forced into enrolment at their next request (server-enforced).
 */
export function TwoFactorPolicyCard() {
  const policy = useTwoFactorPolicy();
  const setPolicy = useSetTwoFactorPolicy();
  const roles = policy.data?.roles ?? [];

  const toggle = (role: UserRole, on: boolean) => {
    const next = on ? Array.from(new Set([...roles, role])) : roles.filter((r) => r !== role);
    setPolicy.mutate({ roles: next });
  };

  return (
    <Card>
      <CardHeader
        title="Two-factor requirement"
        description="Force these roles to enrol in two-factor before they can use the panel."
      />
      <div className="p-5">
        {policy.isPending ? (
          <Spinner />
        ) : policy.error ? (
          <ErrorNote error={policy.error} />
        ) : (
          <div className="space-y-3">
            {POLICY_ROLES.map(({ role, label, description }) => (
              <label key={role} className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={roles.includes(role)}
                  disabled={setPolicy.isPending}
                  onChange={(e) => toggle(role, e.target.checked)}
                />
                <span>
                  <span className="font-medium">{label}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    {description}
                  </span>
                </span>
              </label>
            ))}
            {setPolicy.error != null && <ErrorNote error={setPolicy.error} />}
          </div>
        )}
      </div>
    </Card>
  );
}
